import { Html5Qrcode } from 'html5-qrcode';
import { useEffect, useRef } from 'react';
import scannerBeep from '../assets/mp3/scanner-beep.mp3';

const QR_ELEMENT_ID = 'qr-reader-viewport';

/**
 * useQrScanner
 *
 * Encapsulates all Html5Qrcode lifecycle management:
 *   - Starting / stopping the camera
 *   - One-shot scan lock (prevent duplicate FPS callbacks)
 *   - iOS audio unlock priming
 *   - ResizeObserver error suppression injected by html5-qrcode
 *
 * @param {object} params
 * @param {'display'|'scan'} params.mode        - Current scanner mode
 * @param {Function}         params.setMode      - Mode setter (to revert on error)
 * @param {Function}         params.onScanned    - Called once with the decoded string
 * @param {Function}         params.onPermissionError - Called with a message string on permission failure
 * @param {Function}         params.onScanError  - Called with a message string on non-permission error
 *
 * @returns {{ triggerScan: Function }}
 *   triggerScan — call this from a button's onClick to prime iOS audio + switch to scan mode
 */
const useQrScanner = ({ mode, setMode, onScanned, onPermissionError, onScanError }) => {
  const scannerRef   = useRef(null);  // Html5Qrcode instance
  const isScannedRef = useRef(false); // one-shot lock – resets on every new scan session
  const beepRef      = useRef(new Audio(scannerBeep));

  // ── Suppress ResizeObserver noise injected by html5-qrcode ──────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e?.message?.includes('ResizeObserver')) e.stopImmediatePropagation();
    };
    window.addEventListener('error', handler);
    return () => window.removeEventListener('error', handler);
  }, []);

  // ── Cleanup helper ───────────────────────────────────────────────────────────
  const stopScanner = async () => {
    const inst = scannerRef.current;
    if (!inst) return;
    scannerRef.current = null;
    try {
      if (inst.isScanning) await inst.stop();
    } catch (_) { /* already stopped or never started */ }
    try { inst.clear(); } catch (_) { /* ignore */ }
  };

  // ── Internal decode callback (fires every FPS frame until locked) ────────────
  const handleDecodeSuccess = (decodedText) => {
    if (isScannedRef.current) return;
    isScannedRef.current = true;

    // Play beep immediately on success
    try {
      beepRef.current.currentTime = 0;
      beepRef.current.play().catch(() => { /* autoplay blocked – silently ignore */ });
    } catch (_) { /* ignore */ }

    stopScanner().finally(() => {
      setMode('display');
      onScanned(decodedText);
    });
  };

  // ── Start camera ─────────────────────────────────────────────────────────────
  const startCamera = async () => {
    await stopScanner();
    isScannedRef.current = false;

    // Give React one frame to mount the scanner div into the DOM
    await new Promise((r) => setTimeout(r, 150));

    if (!document.getElementById(QR_ELEMENT_ID)) {
      console.warn('[useQrScanner] QR element not found in DOM');
      setMode('display');
      return;
    }

    const instance = new Html5Qrcode(QR_ELEMENT_ID, { verbose: false });
    scannerRef.current = instance;

    try {
      await instance.start(
        { facingMode: 'environment' },
        { fps: 8 /* qrbox omitted → full-frame scanning, more tolerant */ },
        handleDecodeSuccess,
        () => { /* per-frame failure — not an error, silenced */ }
      );
    } catch (err) {
      console.error('[useQrScanner] start failed:', err);
      scannerRef.current = null;

      const msg = err?.message || 'Failed to start camera.';
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        onPermissionError(
          'Camera access is blocked. Tap the lock icon in your browser address bar, reset permissions, then try again.'
        );
      } else if (err?.name === 'NotFoundError') {
        onPermissionError('No camera was detected on this device.');
      } else {
        onScanError(msg);
      }
      setMode('display');
    }
  };

  // ── React to mode changes ────────────────────────────────────────────────────
  useEffect(() => {
    if (mode === 'scan') {
      startCamera();
    } else {
      stopScanner();
    }
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // ── iOS audio unlock: prime the beep element during a direct user gesture ───
  const triggerScan = () => {
    try {
      const audio = beepRef.current;
      audio.volume = 0;
      const p = audio.play();
      if (p) {
        p.then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = 1;
        }).catch(() => { audio.volume = 1; });
      }
    } catch (_) { /* ignore */ }

    setMode('scan');
  };

  return { triggerScan };
};

export { QR_ELEMENT_ID };
export default useQrScanner;
